import { isEqual } from "lodash-es";

import {
  ChatCustomFieldValueRaw,
  ChatProposalAction,
  ChatRecordUpdateAction,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import { CustomFieldValueType } from "@mapmycustomers/shared/types/customField/CustomFieldValueType";

import ChatCustomFieldValue from "@app/types/chat/ChatCustomFieldValue";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";

import { ADDRESS_CHANGE_KEY, toAddressComponents } from "./chatAddress";
import {
  customFieldValueItems,
  draftCustomFieldsOf,
  toRawCustomFieldValues,
} from "./logActivityDraft";
import { RecordUpdateRow } from "./recordUpdateRows";

export interface RecordUpdateDraft {
  // Every proposed custom field, in the wire's own shape. Full rather than sparse, because the
  // apply treats this array as a whole-set replacement.
  customFields?: ChatCustomFieldValue[];
  // Every proposed standard field, keyed by write column. Full, not sparse — the serializer is the
  // one place that decides what actually changed.
  values: Record<string, unknown>;
}

// The proposal as a draft: what the form opens with, and what an untouched save reproduces.
export const draftFromAction = (action: ChatRecordUpdateAction): RecordUpdateDraft => ({
  ...(action.customFields
    ? {
        customFields: action.customFields.map(({ esKey, value }) => ({ esKey, value })),
      }
    : {}),
  values: Object.fromEntries(
    action.changes.map(({ field, proposedValue }) => [field, proposedValue])
  ),
});

// The draft store is typed Record<string, unknown> and chatConversationStorage restores drafts a
// previous build persisted, so every read is guarded rather than cast.
export const readDraft = (
  draft: Record<string, unknown> | undefined
): RecordUpdateDraft | undefined => {
  if (!draft || typeof draft.values !== "object" || draft.values === null) {
    return undefined;
  }
  return {
    // Present-but-empty and absent are different instructions downstream, so the key is kept or
    // dropped by the draft's own shape; draftCustomFieldsOf then re-checks each entry, because a
    // persisted draft outlives the build that wrote it.
    ...(Array.isArray(draft.customFields) ? { customFields: draftCustomFieldsOf(draft) } : {}),
    values: draft.values as Record<string, unknown>,
  };
};

// Sorted rather than positional: a multi-option field is a set, so a reordering is not an edit, and
// a single-valued field has one element where sorting is a no-op.
export const isSameCustomFieldValue = (
  a: CustomFieldValueType,
  b: CustomFieldValueType
): boolean => {
  const first = customFieldValueItems(a);
  const second = customFieldValueItems(b);
  if (first.length !== second.length) {
    return false;
  }
  const key = (value: unknown) => JSON.stringify(value);
  const left = first.map(key).sort();
  const right = second.map(key).sort();
  return left.every((value, index) => value === right[index]);
};

// By value, not by identity. Every standard proposed value was a scalar until BE folded address
// editing in as one compound object (ENG-1280), and a draft restored from sessionStorage is a fresh
// object — so `===` reported an untouched address as an edit and shipped a redundant finalValues key.
const isSameStandardValue = (drafted: unknown, proposedValue: unknown): boolean =>
  isEqual(drafted, proposedValue);

export const isRecordUpdateDraftEqual = (
  action: ChatProposalAction,
  draft: Record<string, unknown>
): boolean => {
  if (action.type !== "record_update") {
    return false;
  }
  const read = readDraft(draft);
  if (!read) {
    return false;
  }
  const standardUnchanged = action.changes.every(({ field, proposedValue }) =>
    isSameStandardValue(read.values[field], proposedValue)
  );
  if (!standardUnchanged) {
    return false;
  }
  const proposedCustomFields = action.customFields ?? [];
  const draftedCustomFields = read.customFields ?? [];
  if (proposedCustomFields.length !== draftedCustomFields.length) {
    return false;
  }
  return proposedCustomFields.every((proposed) => {
    const drafted = draftedCustomFields.find(({ esKey }) => esKey === proposed.esKey);
    return !!drafted && isSameCustomFieldValue(proposed.value, drafted.value);
  });
};

// The only validation the modal performs beyond type coercion: IField.isNullable decides whether a
// field may be emptied, and Save blocks while any resolved, non-nullable field is empty. A row whose
// field did not resolve renders read-only (see recordUpdateRows.ts), so it can never be the empty one.
export const hasEmptyRequiredField = (rows: RecordUpdateRow[], draft: RecordUpdateDraft): boolean =>
  rows.some(({ field, key }) => {
    if (!field || field.isNullable) {
      return false;
    }
    if (isCustomField(field)) {
      const entry = draft.customFields?.find(({ esKey }) => esKey === field.customFieldData.esKey);
      // [] on this one field is how a clear is represented — same convention
      // fromCustomFieldEditorValue uses.
      return !entry || customFieldValueItems(entry.value).length === 0;
    }
    if (key === ADDRESS_CHANGE_KEY) {
      // An emptied address is a full component object of nulls, not a null — the control keeps its
      // shape however much the rep clears out of it.
      return Object.values(toAddressComponents(draft.values[key])).every((part) => part === null);
    }
    const value = draft.values[key];
    return value === null || value === undefined || value === "";
  });

// The custom-field array to POST, or undefined when the rep changed none of them.
//
// Rebuilt from the PROPOSAL rather than forwarded from the draft, so an unproposed custom field can
// never ride along — the platform would write it, and this release does not offer the rep fields the
// agent did not propose.
//
// Returning undefined rather than [] when nothing survives is load-bearing: [] does not mean "no
// changes" on the apply endpoint, it means "wipe every proposed custom-field edit".
const toRecordUpdateCustomFields = (
  action: ChatRecordUpdateAction,
  draft: RecordUpdateDraft
): ChatCustomFieldValueRaw[] | undefined => {
  const proposed = action.customFields ?? [];
  const drafted = draft.customFields ?? [];
  const editOf = (esKey: string) => drafted.find((entry) => entry.esKey === esKey);
  // A proposed custom field missing from the draft is unedited, not cleared — the array below falls
  // back to the proposed value for it, so it must not count as a change.
  const changed = proposed.some((entry) => {
    const edit = editOf(entry.esKey);
    return !!edit && !isSameCustomFieldValue(entry.value, edit.value);
  });
  if (!changed) {
    return undefined;
  }
  const raw = toRawCustomFieldValues(
    getFieldModelByEntityType(action.entityType),
    proposed.map(({ esKey, value }) => ({ esKey, value: editOf(esKey)?.value ?? value }))
  );
  return raw.length ? raw : undefined;
};

// The sparse finalValues for an accepted record_update, or undefined when the rep changed nothing.
//
// Standard keys are never widened: only a proposed field the rep actually edited travels, because
// the platform iterates the stored proposal and would discard anything else while still reporting
// `applied`. Custom fields obey the opposite rule and are handled by toRawCustomFieldValues.
export const toRecordUpdateFinalValues = (
  action: ChatRecordUpdateAction,
  draft: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  const read = readDraft(draft);
  if (!read) {
    return undefined;
  }
  const finalValues: Record<string, unknown> = {};
  action.changes.forEach(({ field, proposedValue }) => {
    const drafted = read.values[field];
    // A key missing from the persisted draft is not a cleared value — it is a draft that never
    // heard about this field — so it must not write `undefined` and inflate finalValues.
    if (field in read.values && !isSameStandardValue(drafted, proposedValue)) {
      finalValues[field] = drafted;
    }
  });
  const customFields = toRecordUpdateCustomFields(action, read);
  if (customFields) {
    finalValues.customFields = customFields;
  }
  return Object.keys(finalValues).length ? finalValues : undefined;
};
